import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { createBlogSchema } from '../../../../forms/createBlogSchema';
import blogService from '../../../../services/blogService';
import { showToast } from '../../../../utils/showToast';
import FormField from '../../../../components/common/FormField';
import api from '../../../../services/api';
import Loader from '../../../../components/common/Loader';

const CreateBlog: React.FC = () => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageInputMode, setImageInputMode] = useState<'upload' | 'url'>('upload');
  const navigate = useNavigate();
  
  // ✅ Get the blog ID from the URL for edit mode
  const { id } = useParams<{ id: string }>(); 
  const isEditMode = Boolean(id);

  const [initialValues, setInitialValues] = useState({
    title: '',
    content: '',
    metaDescription: '',
    tags: '',
    image: '',
    published: false,
  });
  const [isLoading, setIsLoading] = useState(isEditMode);

  // ✅ Fetch blog data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchBlogData = async () => {
        try {
          const blog = await blogService.getBlogForEdit(id);
          setInitialValues({
            title: blog.title,
            content: blog.content,
            metaDescription: blog.metaDescription,
            tags: blog.tags.join(', '),
            image: blog.image || '',
            published: blog.published,
          });
        } catch (err: any) {
          showToast(err.message, { type: 'error' });
          navigate('admin/manage-blogs');
        } finally {
          setIsLoading(false);
        }
      };
      fetchBlogData();
    }
  }, [id, isEditMode, navigate]);


  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createBlogSchema,
    enableReinitialize: true, // This is key for populating the form in edit mode
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      let finalImageUrl = values.image;
      try {
        setSubmitting(true);
        if (imageInputMode === 'upload' && imageFile) {
          const formData = new FormData();
          formData.append('file', imageFile);
          const uploadRes = await api.post('/upload/cloudinary', formData);
          finalImageUrl = uploadRes.data.url;
        }
        const blogPayload = { ...values, image: finalImageUrl || undefined, tags: values.tags.split(',').map(tag => tag.trim()).filter(Boolean) };

        // ✅ UPDATED: Call the correct service based on the mode
        if (isEditMode && id) {
          await blogService.updateBlog(id, blogPayload);
          showToast('Blog post updated successfully!', { type: 'success' });
        } else {
          await blogService.createBlog(blogPayload);

          showToast('Blog post created successfully!', { type: 'success' });
        }
        
        resetForm();
        navigate('/dashboard/admin/manage-blogs');
      } catch (err: any) {
        showToast(err.message, { type: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (isLoading) return <Loader />;

  return (
    <div className="py-10 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        {/* ✅ UPDATED: Dynamic title */}
        <h1 className="text-2xl font-bold mb-6 text-center">{isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}</h1>
        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <FormField label="Title" name="title" type="text" value={formik.values.title} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.title ? formik.errors.title : undefined} />
          <FormField label="Meta Description (for SEO)" name="metaDescription" type="text" value={formik.values.metaDescription} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.metaDescription ? formik.errors.metaDescription : undefined} />
          <FormField label="Tags (comma-separated)" name="tags" type="text" value={formik.values.tags} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.tags ? formik.errors.tags : undefined} />
          
          {/* ✅ FIX: Replaced Formik's <Field> with a standard <textarea> and wired it up manually */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700">Content</label>
            <textarea
              id="content"
              name="content"
              rows={10}
              value={formik.values.content}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`mt-1 w-full p-2 border rounded-lg ${formik.touched.content && formik.errors.content ? 'border-red-500' : 'border-gray-300'}`}
            />
            {formik.touched.content && formik.errors.content && <div className="text-xs text-red-600">{formik.errors.content}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Featured Image</label>
            <div className="mt-2 flex border-b">
              <button type="button" onClick={() => setImageInputMode('upload')} className={`px-4 py-2 text-sm ${imageInputMode === 'upload' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Upload</button>
              <button type="button" onClick={() => setImageInputMode('url')} className={`px-4 py-2 text-sm ${imageInputMode === 'url' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>URL</button>
            </div>
            <div className="mt-4">
              {imageInputMode === 'upload' ? (
                <input id="imageUpload" name="imageUpload" type="file" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
              ) : (
                <FormField label="Image URL" name="image" type="text" value={formik.values.image} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.image ? formik.errors.image : undefined} />
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <input id="published" name="published" type="checkbox" checked={formik.values.published} onChange={formik.handleChange} className="h-4 w-4 rounded" />
            <label htmlFor="published">Publish Post Immediately</label>
          </div>
          
          <button type="submit" disabled={formik.isSubmitting} className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg ...">
            {/* ✅ UPDATED: Dynamic button text */}
            {formik.isSubmitting ? <Loader /> : (isEditMode ? 'Update Post' : 'Create Post')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateBlog;